/**
 * LoginPage — refonte 9.O.1 Phase I.
 *
 * Branding aligné `docs/branding/kollectivia-brand-system.md` :
 *   - Inter exclusif (aucune font legacy serif).
 *   - Mark/wordmark canonique `<KollectiviaWordmark>` lifté en `components/brand/`.
 *   - Palette : navy-950 (panel gauche), slate (neutres), brand-600 (CTA), white (surface).
 *
 * Skills/plugins appliqués upstream :
 *   - frontend-design : refined minimalism, sobre, pas marketing maximalism.
 *   - ui-ux-pro-max : focus-management, inline validation on blur, error-clarity
 *     (cause+fix), primary-action unique, loading button avec spinner.
 *   - Vercel WIG : focus-visible, autocomplete, spellCheck, touch-manipulation,
 *     min-h-dvh, safe-area-inset, … curly, errors include fix, role/aria-live.
 *
 * Bug fix 9.O.1 Phase I : `signIn` côté AuthContext utilise désormais `noAuth: true`
 * pour skipper `supabase.auth.getSession()` qui pouvait hang silencieusement.
 */

import { type FormEvent, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { KollectiviaWordmark } from "../components/brand/KollectiviaMark";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useAuth } from "../contexts/AuthContext";
import { ApiException } from "../lib/api";
import { getApiErrorMessage, normalizeLang } from "../lib/apiErrorMessage";

// Validation email RFC simplifiée — parité Zod côté backend (cohérent avec IntakeForm 9.N.2).

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const { user, signIn, loading } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const invitation = search.get("tenant_id");
  const destination =
    invitation && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(invitation)
      ? `/auth/accept-invite?tenant_id=${encodeURIComponent(invitation)}`
      : "/";

  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  if (!loading && user) return <Navigate to={destination} replace />;

  // Validation email on blur (per ui-ux-pro-max inline-validation) — pas erreur prématurée à chaque keystroke.
  const emailValid = email === "" || EMAIL_RE.test(email);
  const emailShowError = emailTouched && !emailValid;
  const canSubmit = email !== "" && password !== "" && emailValid && !submitting;

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);

    // Focus first invalid (per ui-ux-pro-max focus-management)
    if (!emailValid) {
      emailRef.current?.focus();
      setEmailTouched(true);
      return;
    }
    if (password === "") {
      passwordRef.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate(destination);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(getApiErrorMessage(err, lang) || t("auth.login.invalid"));
      } else {
        setError(t("errors.generic"));
      }
      // Focus password field after auth error (typical UX — user re-types pwd)
      passwordRef.current?.focus();
      passwordRef.current?.select();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-slate-50 pb-[env(safe-area-inset-bottom)]">
      {/* Skip-link a11y (cohérent avec IntakeChrome) */}
      <a
        href="#main"
        className="focus-visible:ring-brand-500 sr-only rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-900 shadow-md focus-visible:not-sr-only focus-visible:absolute focus-visible:left-4 focus-visible:top-4 focus-visible:z-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        {t("app.skip_to_content")}
      </a>

      <div className="grid flex-1 grid-cols-1 lg:grid-cols-2">
        {/* Panneau gauche — surface sombre brand (desktop only).
            Sobre per frontend-design : wordmark + 1 phrase tagline + footnote, pas de marketing copy. */}
        <aside
          className="bg-navy-950 hidden flex-col justify-between p-12 lg:flex"
          aria-hidden="true"
        >
          {/* Hero brand-forward — markSize=160 + textSize=40 (9.P.1b R1 post-smoke
              PO : taille triplée pour exploiter l'espace navy-950 du panneau lg+
              et faire du mark le pièce visuelle dominante, pas un wordmark discret).
              Ratio texte/mark 0.25 : « Kollectivia » lisible sans dominer le mark.
              `animate="all"` active entrance séquentielle (lignes draw + nodes pop)
              sur mount + idle ambiante (float + breathe). Respect `prefers-reduced-motion`
              natif via @media en index.css. */}
          <div>
            <KollectiviaWordmark markSize={160} textSize={40} tone="light" animate="all" />
          </div>
          <div className="max-w-md space-y-3">
            <p className="text-pretty text-2xl font-semibold leading-snug tracking-tight text-white">
              {t("app.tagline")}
            </p>
          </div>
          <p className="text-xs text-slate-400">
            <span translate="no">© Kollectivia</span> · {t("app.data_residency")}
          </p>
        </aside>

        {/* Panneau droit — formulaire login */}
        <main
          id="main"
          role="main"
          className="flex flex-col items-center justify-center px-6 py-12"
        >
          <div className="w-full max-w-sm space-y-8">
            {/* Mobile header — wordmark + lang switcher (panneau gauche masqué <lg) */}
            <div className="flex items-center justify-between lg:hidden">
              <KollectiviaWordmark markSize={22} />
              <LanguageSwitcher />
            </div>
            <div className="hidden justify-end lg:flex">
              <LanguageSwitcher />
            </div>

            {/* Titre */}
            <header className="space-y-1">
              <h1 className="text-pretty text-2xl font-semibold tracking-tight text-slate-900 sm:text-[28px]">
                {t("auth.login.title")}
              </h1>
              <p className="text-sm text-slate-600">{t("auth.login.subtitle")}</p>
            </header>

            <form onSubmit={onSubmit} noValidate className="space-y-5">
              {/* Email */}
              <div className="space-y-1">
                <label htmlFor={emailId} className="block text-sm font-medium text-slate-800">
                  {t("auth.login.email")}
                </label>
                <input
                  ref={emailRef}
                  id={emailId}
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  spellCheck={false}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  placeholder={t("auth.login.email_placeholder")}
                  disabled={submitting}
                  aria-invalid={emailShowError ? "true" : undefined}
                  className="focus:border-brand-500 focus:ring-brand-500 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-1 aria-[invalid=true]:border-red-400 aria-[invalid=true]:focus:ring-red-400 motion-reduce:transition-none"
                />
                {emailShowError && (
                  <p role="alert" className="text-xs font-medium text-red-700">
                    {t("auth.login.email_invalid")}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label htmlFor={passwordId} className="block text-sm font-medium text-slate-800">
                    {t("auth.login.password")}
                  </label>
                  <Link
                    to="/auth/reset"
                    className="text-brand-600 hover:text-brand-700 focus-visible:ring-brand-500 rounded text-xs underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  >
                    {t("auth.login.forgot")}
                  </Link>
                </div>
                <input
                  ref={passwordRef}
                  id={passwordId}
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  aria-describedby={error ? errorId : undefined}
                  className="focus:border-brand-500 focus:ring-brand-500 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-1 motion-reduce:transition-none"
                />
              </div>

              {/* Error display — role=alert + aria-live polite (per WIG + ui-ux-pro-max) */}
              {error && (
                <p
                  id={errorId}
                  role="alert"
                  aria-live="polite"
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700"
                >
                  {error}
                </p>
              )}

              {/* Submit — primary CTA */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="bg-brand-600 hover:bg-brand-700 active:bg-brand-800 focus-visible:ring-brand-500 inline-flex w-full touch-manipulation items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none motion-reduce:transition-none"
              >
                {submitting && (
                  <span
                    aria-hidden="true"
                    className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white motion-reduce:animate-none"
                  />
                )}
                {submitting ? t("auth.login.submitting") : t("auth.login.submit")}
              </button>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
